import { calendarApi, integrationKey } from '../../lib/calendar-api';
import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { emailApi, emailKey } from '../../lib/email-api';
import { keys } from '../../lib/queries';

export type HomeSyncProvider = 'gmail' | 'google_calendar';
export type HomeSyncProgress = Record<HomeSyncProvider, {
  status: 'syncing' | 'pending' | 'synced' | 'disconnected' | 'error';
  error?: string;
}>;

/** Refresh joins existing sync jobs; only the saved automatic-reply preference
 * can enqueue inference for newly imported mail. No mission run happens here. */
export function useHomeRefresh() {
  const client = useQueryClient();
  const inFlight = useRef<Promise<void> | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string>();
  const [progress, setProgress] = useState<HomeSyncProgress | null>(null);
  const refresh = useCallback((): Promise<void> => {
    if (inFlight.current) return inFlight.current;
    setRefreshing(true);
    setError(undefined);
    setProgress({ gmail: { status: 'syncing' }, google_calendar: { status: 'syncing' } });
    const update = (provider: HomeSyncProvider, state: HomeSyncProgress[HomeSyncProvider]) => {
      setProgress(current => current ? { ...current, [provider]: state } : current);
    };
    const wait = async (
      provider: string,
      runId: string,
      poll: (id: string) => Promise<{ status: string }>,
    ) => {
      const deadline = Date.now() + 45_000;
      while (Date.now() < deadline) {
        const run = await poll(runId);
        if (['completed', 'succeeded'].includes(run.status)) return true;
        if (!['queued', 'running'].includes(run.status))
          throw new Error(`${provider} couldn’t finish refreshing. Try again.`);
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
      // A queued/running job is still valid after the UI polling window ends.
      return false;
    };
    const work = async () => {
      const outcomes = await Promise.allSettled([
        (async () => {
          try {
            const services = await calendarApi.home();
            const connections = (services.calendar.connections ?? [])
              .filter(c => ['active', 'importing'].includes(c.status));
            const completed = await Promise.all(
              connections
                .map(async (c) => {
                  const run = await calendarApi.sync(c.id);
                  return wait('Calendar', run.syncRunId, calendarApi.run);
                }),
            );
            update('google_calendar', { status: !connections.length ? 'disconnected' : completed.every(Boolean) ? 'synced' : 'pending' });
          } catch (cause) {
            update('google_calendar', { status: 'error', error: cause instanceof Error ? cause.message : 'Calendar couldn’t sync.' });
            throw cause;
          } finally {
            await client.invalidateQueries({ queryKey: integrationKey() });
          }
        })(),
        (async () => {
          try {
            const settings = await emailApi.connections();
            const connections = settings.connections.filter(c => ['active', 'importing'].includes(c.status));
            const completed = await Promise.all(
              connections
                .map(async (c) => {
                  const run = await emailApi.sync(c.id);
                  return wait('Gmail', run.syncRunId, emailApi.syncRun);
                }),
            );
            update('gmail', { status: !connections.length ? 'disconnected' : completed.every(Boolean) ? 'synced' : 'pending' });
          } catch (cause) {
            update('gmail', { status: 'error', error: cause instanceof Error ? cause.message : 'Gmail couldn’t sync.' });
            throw cause;
          } finally {
            await client.invalidateQueries({ queryKey: emailKey() });
          }
        })(),
      ]);
      const failures = outcomes.filter(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      );
      if (failures.length)
        throw new Error(
          failures
            .map((r) =>
              r.reason instanceof Error
                ? r.reason.message
                : 'Couldn’t refresh service.',
            )
            .join(' '),
        );
    };
    inFlight.current = work()
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error
            ? cause.message
            : 'Couldn’t refresh Home. Try again.',
        );
        throw cause;
      })
      .finally(async () => {
        await Promise.all([
          client.invalidateQueries({ queryKey: emailKey() }),
          client.invalidateQueries({ queryKey: keys.dailyBrief }),
          client.invalidateQueries({ queryKey: keys.personalization }),
        ]);
        inFlight.current = null;
        setRefreshing(false);
      });
    return inFlight.current;
  }, [client]);
  return { refresh, refreshing, error, progress };
}
